import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { label: '企业列表', path: '/', icon: 'ri-building-2-line' },
  { label: '电销中心', path: '/telemarketing', icon: 'ri-phone-line' },
  { label: '数据导入', path: '/import', icon: 'ri-file-upload-line' },
  { label: '标签管理', path: '/tags', icon: 'ri-price-tag-3-line' },
  { label: '通知中心', path: '/notifications', icon: 'ri-notification-3-line' },
];

// 仅系统管理员可见的导航项
const adminNavItems = [
  { label: '企微邀请建号', path: '/settings/wecom-invite', icon: 'ri-user-add-line' },
];

// 移动端底部导航使用短标签,节省横向空间
const mobileNavItems = [
  { label: '企业', path: '/', icon: 'ri-building-2-line' },
  { label: '电销', path: '/telemarketing', icon: 'ri-phone-line' },
  { label: '导入', path: '/import', icon: 'ri-file-upload-line' },
  { label: '标签', path: '/tags', icon: 'ri-price-tag-3-line' },
  { label: '通知', path: '/notifications', icon: 'ri-notification-3-line' },
];

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === '系统管理员';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    navigate('/login');
  };

  const userInitial = user?.name?.charAt(0) || '用';

  return (
    <div className="flex h-screen bg-background-100 overflow-hidden">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-foreground-950/30 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - 桌面端(lg+)常驻,移动端隐藏(改用底部导航 + 抽屉) */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 flex-col bg-background-50 border-r border-background-200/70 transition-all duration-300 hidden lg:flex ${
          sidebarCollapsed ? 'w-[72px]' : 'w-[240px]'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-background-200/70">
          <Link to="/" className="flex items-center gap-3 overflow-hidden whitespace-nowrap">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
              <i className="ri-leaf-line text-lg text-background-50"></i>
            </div>
            {!sidebarCollapsed && (
              <span className="font-heading font-semibold text-foreground-900 text-base">双碳CRM</span>
            )}
          </Link>
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 whitespace-nowrap ${
                isActive(item.path)
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
              }`}
            >
              <i className={`${item.icon} text-lg flex-shrink-0 w-5 h-5 flex items-center justify-center`}></i>
              {!sidebarCollapsed && <span>{item.label}</span>}
            </Link>
          ))}
          {isAdmin && (
            <>
              {!sidebarCollapsed && (
                <p className="px-3 pt-3 pb-1 text-xs font-medium text-foreground-400 uppercase tracking-wider">管理员</p>
              )}
              {adminNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 whitespace-nowrap ${
                    isActive(item.path)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                  }`}
                >
                  <i className={`${item.icon} text-lg flex-shrink-0 w-5 h-5 flex items-center justify-center`}></i>
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </Link>
              ))}
            </>
          )}
        </nav>

        {/* Collapse toggle + User */}
        <div className="border-t border-background-200/70 p-3">
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
            {!sidebarCollapsed && (
              <Link to="/profile" className="flex items-center gap-2.5 min-w-0 cursor-pointer hover:opacity-80 transition-opacity">
                <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-secondary-700">{userInitial}</span>
                </div>
                <span className="text-sm text-foreground-700 truncate">{user?.name || '未知用户'}</span>
              </Link>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex w-8 h-8 items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 hover:text-foreground-700 transition-colors flex-shrink-0 cursor-pointer"
            >
              {sidebarCollapsed ? (
                <i className="ri-arrow-right-s-line text-lg"></i>
              ) : (
                <i className="ri-arrow-left-s-line text-lg"></i>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar drawer - 汉堡菜单打开的完整抽屉 */}
      {mobileMenuOpen && (
        <aside className="fixed inset-y-0 left-0 z-50 flex flex-col bg-background-50 border-r border-background-200/70 w-[260px] lg:hidden">
          <div className="flex items-center h-16 px-4 border-b border-background-200/70 justify-between">
            <Link to="/" className="flex items-center gap-3" onClick={() => setMobileMenuOpen(false)}>
              <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-leaf-line text-lg text-background-50"></i>
              </div>
              <span className="font-heading font-semibold text-foreground-900 text-base">双碳CRM</span>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActive(item.path)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                }`}
              >
                <i className={`${item.icon} text-lg flex-shrink-0 w-5 h-5 flex items-center justify-center`}></i>
                <span>{item.label}</span>
              </Link>
            ))}
            {isAdmin && (
              <>
                <p className="px-3 pt-3 pb-1 text-xs font-medium text-foreground-400 uppercase tracking-wider">管理员</p>
                {adminNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                      isActive(item.path)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                    }`}
                  >
                    <i className={`${item.icon} text-lg flex-shrink-0 w-5 h-5 flex items-center justify-center`}></i>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </>
            )}
          </nav>

          <div className="border-t border-background-200/70 p-4">
            <Link
              to="/profile"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 mb-3 cursor-pointer hover:opacity-80"
            >
              <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-secondary-700">{userInitial}</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground-800">{user?.name || '未知用户'}</p>
                <p className="text-xs text-foreground-500">个人中心</p>
              </div>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-r-line text-base"></i>
              退出登录
            </button>
          </div>
        </aside>
      )}

      {/* Main content - 移动端底部留白避开底部导航栏 */}
      <div className="flex-1 flex flex-col min-w-0 pb-14 lg:pb-0">
        {/* Top bar */}
        <header className="h-14 lg:h-16 bg-background-50 border-b border-background-200/70 flex items-center justify-between px-3 md:px-6 flex-shrink-0">
          <button
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-600 cursor-pointer"
            onClick={() => setMobileMenuOpen(true)}
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            {/* Mobile user menu */}
            <div className="relative lg:hidden">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center cursor-pointer"
              >
                <span className="text-xs font-semibold text-secondary-700">{userInitial}</span>
              </button>
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-background-50 rounded-xl border border-background-200/70 shadow-sm py-1 min-w-[160px]">
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-user-line text-base"></i>
                      个人中心
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-logout-box-r-line text-base"></i>
                      退出登录
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Desktop logout */}
            <button
              className="hidden lg:flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              onClick={handleLogout}
            >
              <i className="ri-logout-box-r-line text-base"></i>
              <span>退出登录</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {/* Mobile bottom navigation bar */}
      <nav className="fixed bottom-0 inset-x-0 z-50 bg-background-50 border-t border-background-200/70 flex items-center justify-around lg:hidden safe-area-bottom">
        {mobileNavItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 min-w-0 flex-1 transition-colors ${
              isActive(item.path)
                ? 'text-primary-600'
                : 'text-foreground-400 hover:text-foreground-600'
            }`}
          >
            <i className={`${item.icon} text-lg`}></i>
            <span className="text-[10px] font-medium leading-none">{item.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}
